import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings as SettingsIcon,
  Calculator,
  PanelLeft,
  PanelRight,
  PanelTop,
  PanelBottom,
  CheckCircle2,
  Layout,
  Smartphone,
  QrCode,
  Mail,
  ArrowRight,
  Layers,
  CalendarClock,
  FileSpreadsheet,
  KeyRound,
  BadgeCheck,
  Hash,
  ShieldAlert,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import mobileDeviceApi from '../services/mobileDeviceApi';
import MobileDeviceSettings from './MobileDeviceSettings';
import DeclaredPassSettings from './DeclaredPassSettings';
import QrPassSettings from './QrPassSettings';

const NAV_LAYOUT_OPTIONS = [
  {
    id: 'left',
    title: 'Sol Menü (Varsayılan)',
    subtitle: 'Klasik dikey akordiyon menü solda yer alır.',
    icon: PanelLeft,
    badge: 'Önerilen'
  },
  {
    id: 'right',
    title: 'Sağ Menü',
    subtitle: 'Dikey akordiyon menü ekranın sağ kenarına sabitlenir.',
    icon: PanelRight
  },
  {
    id: 'top',
    title: 'Üst Yatay Menü (Tam Genişlik)',
    subtitle: 'Geniş puantaj ve rapor tabloları için %100 ekran genişliği sağlar.',
    icon: PanelTop
  },
  {
    id: 'bottom',
    title: 'Alt Dock Menü',
    subtitle: 'Modern uygulama dock görünümü; menü ekranın altına sabitlenir.',
    icon: PanelBottom
  }
];

const LOGIN_IDENTIFIER_OPTIONS = [
  {
    id: 'TCKN',
    title: 'T.C. Kimlik Numarası ile Giriş',
    badge: 'Varsayılan',
    icon: BadgeCheck,
    example: 'Örn: 11479186782',
    description:
      'Personeller Web ve Mobil uygulamaya 11 haneli T.C. Kimlik Numaraları ile giriş yapar. Yeni eklenen personelin ilk şifresi T.C. Kimlik Numarası olarak atanır ve ilk girişte şifre değiştirmesi zorunlu tutulur.'
  },
  {
    id: 'SICIL',
    title: 'Sicil Numarası ile Giriş',
    badge: 'Kurumsal Sicil',
    icon: Hash,
    example: 'Örn: PRS-2026-0001 veya 10045',
    description:
      'Personeller sisteme Sicil Numaraları ile giriş yapar. Bu seçeneğe geçildiğinde mevcut tüm personellerin kullanıcı adları otomatik olarak Sicil Numaralarına senkronize edilir ve personel kartında Sicil No alanı kilitlenerek korumaya alınır.'
  }
];

const SETTINGS_TABS = [
  { id: 'all', label: 'Tüm Ayarlar', icon: Layers },
  { id: 'auth_policy', label: 'Personel Giriş & Kimlik Politikası', icon: KeyRound },
  { id: 'mobile_security', label: 'Mobil Cihaz & Çoklu Cihaz Güvenliği', icon: Smartphone },
  { id: 'pass_policies', label: 'Beyanlı & QR / Orion Geçiş', icon: QrCode },
  { id: 'appearance', label: 'Görünüm & Sistem Kısayolları', icon: Layout }
];

export default function SettingsPanel({
  navPosition = 'left',
  onChangeNavPosition,
  isSidebarCollapsed,
  onToggleSidebar,
  onNavigate
}) {
  const { firmCode, isAuthenticated, permissions = [] } = useAuth();
  const [activeTab, setActiveTab] = useState('all');

  // Personel Giriş Kullanıcı Adı Politikası (TCKN / SICIL)
  const [loginIdentifierMode, setLoginIdentifierMode] = useState('TCKN');
  const [savedIdentifierMode, setSavedIdentifierMode] = useState('TCKN');
  const [loginModeLoading, setLoginModeLoading] = useState(false);
  const [loginModeSaving, setLoginModeSaving] = useState(false);
  const [loginModeFeedback, setLoginModeFeedback] = useState(null);

  const canManageAuthPolicy =
    permissions.includes('settings:manage') ||
    permissions.includes('mobile_devices:manage_policy') ||
    permissions.includes('personnel:update');

  const fetchFirmAuthPolicy = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoginModeLoading(true);
    try {
      const settings = await mobileDeviceApi.getSettings();
      const rawMode = (settings?.['Auth.LoginIdentifierMode'] || 'TCKN').toString().trim().toUpperCase();
      const normalized = rawMode === 'SICIL' ? 'SICIL' : 'TCKN';
      setLoginIdentifierMode(normalized);
      setSavedIdentifierMode(normalized);
    } catch (err) {
      console.error('Firma giriş politikası alınamadı:', err);
    } finally {
      setLoginModeLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchFirmAuthPolicy();
  }, [fetchFirmAuthPolicy]);

  const handleSaveLoginIdentifierMode = async (targetMode) => {
    if (!canManageAuthPolicy || loginModeSaving) return;
    const normalized = targetMode === 'SICIL' ? 'SICIL' : 'TCKN';
    setLoginModeSaving(true);
    setLoginModeFeedback(null);
    try {
      const res = await mobileDeviceApi.updateSetting('Auth.LoginIdentifierMode', normalized);
      setLoginIdentifierMode(normalized);
      setSavedIdentifierMode(normalized);
      setLoginModeFeedback({
        type: 'success',
        text:
          res?.message ||
          `Personel giriş kullanıcı adı politikası '${normalized === 'SICIL' ? 'Sicil Numarası' : 'T.C. Kimlik Numarası'}' olarak güncellendi.`
      });
    } catch (err) {
      setLoginModeFeedback({
        type: 'error',
        text: err?.message || 'Giriş politikası güncellenirken bir hata oluştu.'
      });
    } finally {
      setLoginModeSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6 bg-white dark:bg-[#18191c] rounded-2xl shadow-md min-h-[80vh] flex flex-col items-center justify-center text-gray-700 dark:text-gray-300">
        <h2 className="text-2xl font-bold mb-4">Erişim Reddedildi</h2>
        <p>Ayarları görüntülemek için lütfen giriş yapın.</p>
      </div>
    );
  }

  const navigateTo = (pageKey) => {
    if (onNavigate) {
      onNavigate(pageKey);
    } else {
      window.location.hash = `#/${pageKey}`;
    }
  };

  const showAuthPolicy = activeTab === 'all' || activeTab === 'auth_policy';
  const showAppearance = activeTab === 'all' || activeTab === 'appearance';
  const showPassPolicies = activeTab === 'all' || activeTab === 'pass_policies';
  const showMobileSecurity = activeTab === 'all' || activeTab === 'mobile_security';

  return (
    <div className="space-y-5">
      {/* Üst Başlık ve Kategori Sekmeleri */}
      <div className="p-5 bg-white dark:bg-[#18191c] rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-gray-200/80 dark:border-gray-800">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
              <SettingsIcon className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Genel Ayarlar</h2>
                {firmCode && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
                    Firma: {firmCode}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Mobil cihaz güvenlik politikalarını, çoklu cihaz izinlerini, beyanlı/QR geçiş kurallarını ve arayüz yerleşimini tek merkezden yönetin.
              </p>
            </div>
          </div>
        </div>

        {/* Kategori Filtre Sekmeleri */}
        <div className="flex items-center gap-2 overflow-x-auto pt-4">
          {SETTINGS_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-[#111214] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200/70 dark:border-gray-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 0. PERSONEL GİRİŞ & KULLANICI ADI POLİTİKASI (T.C. KİMLİK NO / SİCİL NO) */}
      {showAuthPolicy && (
        <section className="p-5 bg-white dark:bg-[#18191c] rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Personel Sisteme Giriş &amp; Kullanıcı Adı Politikası
                  </h3>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200/70 dark:border-indigo-500/30">
                    Aktif Mod: {savedIdentifierMode === 'SICIL' ? 'Sicil Numarası' : 'T.C. Kimlik Numarası'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Personellerin Web ve Mobil uygulamaya giriş yaparken kullanıcı adı olarak <strong>T.C. Kimlik Numarası</strong> mı yoksa <strong>Sicil Numarası</strong> mı kullanacağını belirleyin.
                </p>
              </div>
            </div>

            {loginIdentifierMode !== savedIdentifierMode && canManageAuthPolicy && (
              <button
                type="button"
                disabled={loginModeSaving}
                onClick={() => handleSaveLoginIdentifierMode(loginIdentifierMode)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-all disabled:opacity-60"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loginModeSaving ? 'animate-spin' : ''}`} />
                {loginModeSaving ? 'Senkronize Ediliyor...' : 'Politikayı Kaydet & Tüm Personelleri Senkronize Et'}
              </button>
            )}
          </div>

          {loginModeFeedback && (
            <div
              className={`mb-4 p-3 rounded-xl border text-xs font-medium flex items-center justify-between gap-2 ${
                loginModeFeedback.type === 'success'
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                  : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200'
              }`}
            >
              <span>{loginModeFeedback.text}</span>
              <button
                type="button"
                onClick={() => setLoginModeFeedback(null)}
                className="text-[11px] underline opacity-75 hover:opacity-100"
              >
                Kapat
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {LOGIN_IDENTIFIER_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = loginIdentifierMode === opt.id;
              const isSaved = savedIdentifierMode === opt.id;

              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={!canManageAuthPolicy || loginModeLoading || loginModeSaving}
                  onClick={() => {
                    setLoginIdentifierMode(opt.id);
                    if (opt.id !== savedIdentifierMode) {
                      handleSaveLoginIdentifierMode(opt.id);
                    }
                  }}
                  className={`group relative flex flex-col justify-between p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                    isSelected
                      ? 'bg-indigo-50/60 dark:bg-indigo-950/25 border-indigo-500 shadow-sm ring-1 ring-indigo-500/20'
                      : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200 dark:border-gray-800 hover:border-indigo-400/60 dark:hover:border-indigo-500/50'
                  } ${!canManageAuthPolicy ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                            isSelected
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200/80 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-bold ${
                                isSelected ? 'text-indigo-950 dark:text-indigo-200' : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {opt.title}
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                              {opt.badge}
                            </span>
                          </div>
                          <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">{opt.example}</span>
                        </div>
                      </div>
                      {isSaved && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Aktif
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{opt.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-3.5 p-3 rounded-xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-800/50 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
              <strong>Güvenlik &amp; Senkronizasyon Bilgisi:</strong> Yeni personel eklendiğinde başlangıç şifresi her zaman personelin <strong>T.C. Kimlik Numarası</strong> olarak atanır ve <strong>İlk Girişte Şifre Değiştirme Zorunluluğu</strong> aktif edilir. Sicil No ile giriş seçildiğinde, personel düzenleme ekranında Sicil Numarası yanlışlıkla değiştirilmeye karşı kilitli gelir; yetkili tarafından kilit açılıp Sicil No güncellenirse personelin giriş kullanıcı adı da otomatik olarak yeni Sicil Numarasına güncellenir.
            </p>
          </div>
        </section>
      )}

      {/* 1. MENÜ (NAVBAR) KONUMU VE GÖRÜNÜM AYARLARI */}
      {showAppearance && (
        <section className="p-5 bg-white dark:bg-[#18191c] rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                <Layout className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Menü (Navbar) Konumu ve Yerleşim Tercihi
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Navigasyon barını çalışma alışkanlığınıza göre <strong>Solda</strong>, <strong>Sağda</strong>, <strong>Üstte</strong> veya <strong>Altta</strong> kullanabilirsiniz. Seçiminiz anında uygulanır ve tarayıcınızda hatırlanır.
                </p>
              </div>
            </div>
            {(navPosition === 'left' || navPosition === 'right') && onToggleSidebar && (
              <button
                type="button"
                onClick={onToggleSidebar}
                className="self-start sm:self-auto px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {isSidebarCollapsed ? 'Dikey Menüyü Genişlet' : 'Dikey Menüyü İkon Moduna Daralt'}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            {NAV_LAYOUT_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = navPosition === opt.id;

              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onChangeNavPosition?.(opt.id)}
                  className={`group relative flex flex-col justify-between p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                    isSelected
                      ? 'bg-emerald-50/70 dark:bg-emerald-950/25 border-emerald-500 shadow-sm ring-1 ring-emerald-500/20'
                      : 'bg-gray-50/70 dark:bg-[#111214] border-gray-200 dark:border-gray-800 hover:border-emerald-400/60 dark:hover:border-emerald-500/50'
                  }`}
                >
                  <div>
                    {/* Mini Ekran Önizleme Çizimi */}
                    <div className="w-full h-20 mb-3 rounded-xl bg-gray-200/70 dark:bg-[#18191c] border border-gray-200 dark:border-gray-800 p-1.5 flex relative overflow-hidden">
                      {opt.id === 'left' && (
                        <>
                          <div className={`w-4 h-full rounded-md ${isSelected ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                          <div className="flex-1 ml-1.5 rounded-md bg-white dark:bg-[#111214] border border-gray-200/60 dark:border-gray-800" />
                        </>
                      )}
                      {opt.id === 'right' && (
                        <>
                          <div className="flex-1 mr-1.5 rounded-md bg-white dark:bg-[#111214] border border-gray-200/60 dark:border-gray-800" />
                          <div className={`w-4 h-full rounded-md ${isSelected ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                        </>
                      )}
                      {opt.id === 'top' && (
                        <div className="w-full h-full flex flex-col">
                          <div className={`w-full h-3.5 rounded-md ${isSelected ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                          <div className="flex-1 mt-1.5 rounded-md bg-white dark:bg-[#111214] border border-gray-200/60 dark:border-gray-800" />
                        </div>
                      )}
                      {opt.id === 'bottom' && (
                        <div className="w-full h-full flex flex-col">
                          <div className="flex-1 mb-1.5 rounded-md bg-white dark:bg-[#111214] border border-gray-200/60 dark:border-gray-800" />
                          <div className={`w-full h-3.5 rounded-md ${isSelected ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`} />
                        <span className={`text-sm font-bold ${isSelected ? 'text-emerald-900 dark:text-emerald-200' : 'text-gray-900 dark:text-white'}`}>
                          {opt.title}
                        </span>
                      </div>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />}
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      {opt.subtitle}
                    </p>
                  </div>

                  {opt.badge && (
                    <div className="mt-3 pt-2 border-t border-gray-200/60 dark:border-gray-800 flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                        {opt.badge}
                      </span>
                      {isSelected && <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Aktif</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* 2. MOBİL CİHAZ YÖNETİMİ & ÇOKLU CİHAZ POLİTİKASI */}
      {showMobileSecurity && (
        <section>
          <MobileDeviceSettings />
        </section>
      )}

      {/* 3. BEYANLI GEÇİŞ VE QR / ORION GEÇİŞ AYARLARI */}
      {showPassPolicies && (
        <>
          <section>
            <DeclaredPassSettings />
          </section>
          <section>
            <QrPassSettings />
          </section>
        </>
      )}

      {/* 4. SİSTEM KISAYOLLARI (OTOMATİK HESAPLAMA & MAİL / BİLDİRİM MERKEZİ) */}
      {showAppearance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Otomatik Hesaplama Ayarları */}
          <div className="p-5 bg-white dark:bg-[#18191c] rounded-2xl border border-gray-200 dark:border-gray-800 flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Calculator className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Otomatik Puantaj Hesaplama Ayarları
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                    Personel puantajlarının arka planda periyodik olarak otomatik hesaplanmasını ve geriye dönük tarama günlerini yapılandırın.
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end">
              <button
                type="button"
                onClick={() => navigateTo('payroll_calc_settings')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border border-emerald-200/70 dark:border-emerald-500/30 transition-colors"
              >
                Hesaplama Ayarlarına Git
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Mail & Rapor Abonelik Merkezi Kısayolları */}
          <div className="p-5 bg-white dark:bg-[#18191c] rounded-2xl border border-gray-200 dark:border-gray-800 flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Mail & Otomatik Rapor Bildirim Merkezi
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                    SMTP sunucu ayarlarını, zamanlanmış Excel rapor aboneliklerini ve mail gönderim geçmişini yönetin.
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => navigateTo('mail_settings')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-[#111214] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
              >
                <Mail className="w-3.5 h-3.5 text-indigo-500" />
                SMTP Ayarları
              </button>
              <button
                type="button"
                onClick={() => navigateTo('mail_subscriptions')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-[#111214] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
              >
                <CalendarClock className="w-3.5 h-3.5 text-emerald-500" />
                Rapor Abonelikleri
              </button>
              <button
                type="button"
                onClick={() => navigateTo('mail_dispatch_logs')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-200/70 dark:border-indigo-500/30 transition-colors"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Gönderim Logları
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
